import React, { useState, useContext, useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Container, TextField, Typography, Box, Divider, Paper, Stack } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import UserContext from './UserContext';
import { logEvent } from 'firebase/analytics';
import { toast } from './toast';
import EmailIcon from '@mui/icons-material/Email';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';

const GoogleColorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, auth, analytics } = useContext(UserContext);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || '/';

  useEffect(() => {
    document.title = "Forma | Log In or Sign Up";
    if (user) {
      navigate(from);
    }
  }, [user, navigate, from]);

  const validateInput = () => {
    const isEmailEmpty = !email;
    const isPasswordEmpty = !password;
    setEmailError(isEmailEmpty);
    setPasswordError(isPasswordEmpty);
    return !isEmailEmpty && !isPasswordEmpty;
  };

  const getFriendlyErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/user-not-found':
        return 'Email not found.'
      case 'auth/wrong-password':
        return 'Wrong password.';
      case 'auth/invalid-password':
        return 'Invalid password.';
      case 'auth/email-already-exists':
      case 'auth/email-already-in-use':
        return 'This email is already in use by another account.';
      case 'auth/weak-password':
        return 'Try a stronger password.';
      case 'auth/popup-blocked':
        return 'Popup blocked by browser.'
      case 'auth/user-disabled':
        return 'Account is disabled. Please contact support.';
      default:
        return 'An unexpected error occurred. Please retry or contact support.';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateInput()) {
      return;
    }

    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      toast.success(`Signed in as ${result.user.email}.`);
      logEvent(analytics, 'sign_in_success');
    } catch (err) {
      console.error(err);
      toast.error(getFriendlyErrorMessage(err.code));
      logEvent(analytics, 'sign_in_error', {error: err.message});
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    if (!validateInput()) {
      return;
    }

    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      toast.success(`Signed in as ${result.user.email}.`);
      logEvent(analytics, 'sign_up_success');
    } catch (err) {
      console.error(err);
      toast.error(getFriendlyErrorMessage(err.code));
      logEvent(analytics, 'sign_up_error', {error: err.message});
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    // setLoading(true);
    try {
      await signInWithPopup(auth, provider);
      logEvent(analytics, 'google_sign_in_success');
      navigate(from);
    } catch (err) {
      console.error(err);
      logEvent(analytics, 'google_sign_in_error', { error: err.message });
      if (err.code !== 'auth/cancelled-popup-request' && err.code !== 'auth/popup-closed-by-user') {
        toast.error('Failed to sign in with Google.');
      }
    }
    // finally {
    //   setLoading(false);
    // }
  };

  return (
    <Container maxWidth="xs">
      <Box display="flex" flexDirection="column" alignItems="center" marginTop={10}>
        <Typography variant="h4" align="center" fontWeight={600}>
          Log in or sign up
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mt: 0.5, mb: 0 }}>
          Get free expert analysis of your running form
        </Typography>
        <Paper elevation={1} sx={{ p: { xs: 2, sm: 4 }, width: '100%', borderRadius: 2, mt: 2 }}>
        <form onSubmit={handleSubmit} noValidate autoComplete="on">
          <Stack spacing={2}>
            <TextField
              error={emailError}
              helperText={emailError ? "Email is required" : ""}
              variant="outlined"
              required
              fullWidth
              id="email"
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              error={passwordError}
              helperText={passwordError ? "Password is required" : ""}
              variant="outlined"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<EmailIcon />}
              sx={{ textTransform: 'none' }}
              loading={loading}
            >
              Log in with email
            </LoadingButton>
            <LoadingButton
              type="button"
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<EmailIcon />}
              sx={{ textTransform: 'none' }}
              onClick={handleSignUp}
              loading={loading}
            >
              Sign up with email
            </LoadingButton>
            <Divider>
              <Typography variant='body2' color="textSecondary">or</Typography>
            </Divider>
            <LoadingButton
              type="button"
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<GoogleColorIcon />}
              sx={{ textTransform: 'none' }}
              onClick={handleGoogleSignIn}
              loading={loading}
            >
              Continue with Google
            </LoadingButton>
          </Stack>
        </form>
        </Paper>
        <Box display="flex" alignItems="center" gap={0.75} sx={{ mt: 2, color: 'text.secondary' }}>
          <LockOutlinedIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">
            We never share your information with third parties.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}

export default SignInPage;
